import type {
  AdminBlackjackConfigProfile,
  AuthenticatedAdmin,
} from "@utopia-well-done-ltd/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, type BlackjackOperationsResponse } from "../api/client.js";
import { can } from "../capabilities.js";
import { ApprovedConfigProfiles } from "../components/ApprovedConfigProfiles.js";
import { OperatorProfiles } from "../components/OperatorProfiles.js";
import { asAmount, asDuration, asMoment } from "../format.js";

const REFRESH_MS = 10_000;

function wagerBand(
  band: AdminBlackjackConfigProfile["config"]["mainWager"],
  currency: AdminBlackjackConfigProfile["config"]["currency"]["code"],
) {
  const amount = (value: string) => asAmount({ amount: value, currency });
  return `${amount(band.minimum)} – ${amount(band.maximum)} · chips ${band.chipDenominations.map(amount).join(", ")}`;
}

export function BlackjackOperations({
  admin,
  onOpenRound,
}: {
  admin: AuthenticatedAdmin;
  onOpenRound?: (roundId: string) => void;
}) {
  const [operatorProfilesEnabled, setOperatorProfilesEnabled] = useState(false);
  const [data, setData] = useState<BlackjackOperationsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState<string | null>(null);
  const request = useRef<AbortController | null>(null);

  const load = useCallback(async (polling = false) => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setError(null);
    try {
      setData(await api.blackjackOperations(controller.signal, polling));
    } catch (caught) {
      if (!controller.signal.aborted)
        setError(
          caught instanceof ApiError ? caught.message : "Could not load Blackjack operations.",
        );
    } finally {
      if (request.current === controller) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const interval = window.setInterval(() => void load(true), REFRESH_MS);
    return () => {
      window.clearInterval(interval);
      request.current?.abort();
    };
  }, [load]);

  async function activateProfile(
    table: BlackjackOperationsResponse["tables"][number],
    profile: AdminBlackjackConfigProfile,
    reason: string,
  ) {
    const result = await api.activateBlackjackConfigProfile({
      expectedActive: table.configuration.active,
      expectedPending: null,
      reason,
      tableId: table.tableId,
      target: {
        configId: profile.config.configId,
        configSha256: profile.config.configSha256,
      },
    });
    setSaved(
      result.outcome === "scheduled"
        ? `${profile.displayName} is pending the next safe table boundary.`
        : result.outcome === "already_active"
          ? `${profile.displayName} was already active.`
          : `${profile.displayName} is active.`,
    );
    await load();
  }

  async function cancelPendingProfile(
    table: BlackjackOperationsResponse["tables"][number],
    reason: string,
  ) {
    const pending = table.configuration.pending;
    if (!pending) throw new Error("There is no pending Blackjack profile to cancel.");
    await api.cancelBlackjackConfigProfile({
      expectedActive: table.configuration.active,
      expectedPending: pending.target,
      reason,
      tableId: table.tableId,
    });
    const pendingName =
      table.configuration.approvedProfiles.find(
        (profile) =>
          profile.config.configId === pending.target.configId &&
          profile.config.configSha256 === pending.target.configSha256,
      )?.displayName ?? pending.target.configId;
    setSaved(`${pendingName} was removed from the activation queue.`);
    await load();
  }

  if (loading && !data) return <p className="empty">Loading Blackjack operations…</p>;
  if (!data)
    return (
      <section className="body">
        <p className="banner" role="alert">
          {error}
        </p>
        <button className="btn btn-quiet" onClick={() => void load()} type="button">
          Retry
        </button>
      </section>
    );

  const occupied = data.tables.reduce((count, table) => count + table.seats.length, 0);
  const connected = data.tables.reduce(
    (count, table) => count + table.seats.filter((seat) => seat.connected).length,
    0,
  );
  return (
    <section className="body poker-operations">
      <OperatorProfiles
        game="blackjack"
        role={admin.role}
        onEnabled={setOperatorProfilesEnabled}
        onChanged={() => load()}
      />
      <div className="poker-refresh-state">
        <span>Snapshot {asMoment(data.sampledAt)}</span>
        <span>Auto-refreshes every 10 seconds</span>
      </div>
      {error ? (
        <div className="banner" role="alert">
          Latest refresh failed. {error}
        </div>
      ) : null}
      {saved ? (
        <div className="banner banner-quiet" role="status">
          {saved}
        </div>
      ) : null}
      <div className="poker-status-grid">
        <article className="card poker-status-card">
          <span className="label-13 dim">Authority</span>
          <strong>{data.status}</strong>
          <small>{data.tables.length} tables</small>
        </article>
        <article className="card poker-status-card">
          <span className="label-13 dim">Engine</span>
          <strong>{data.engineVersion}</strong>
          <small>runtime</small>
        </article>
        <article className="card poker-status-card">
          <span className="label-13 dim">Seats</span>
          <strong>
            {connected}/{occupied}
          </strong>
          <small>connected / occupied</small>
        </article>
        <article className="card poker-status-card">
          <span className="label-13 dim">Active rounds</span>
          <strong>{data.tables.filter((table) => table.currentRound).length}</strong>
          <small>in progress</small>
        </article>
      </div>

      {data.tables.map((table) => (
        <div className="card poker-table-summary" key={table.tableId}>
          <div className="card-bar">
            <strong>{table.tableId}</strong>
            <span className="chip">{table.phase}</span>
          </div>
          <dl className="poker-facts">
            <div>
              <dt>Current round</dt>
              <dd className="mono-12">{table.currentRound?.roundId ?? "None"}</dd>
            </div>
            <div>
              <dt>Revision</dt>
              <dd>{table.revision}</dd>
            </div>
            <div>
              <dt>Events</dt>
              <dd>{table.eventSequence}</dd>
            </div>
            <div>
              <dt>Config</dt>
              <dd className="mono-12">{table.configuration.active.configId}</dd>
            </div>
            <div>
              <dt>Updated</dt>
              <dd>{asMoment(table.updatedAt)}</dd>
            </div>
          </dl>
          <ApprovedConfigProfiles
            operatorProfilesEnabled={operatorProfilesEnabled}
            canWrite={
              can(admin.role, "games.config.activate") ||
              (operatorProfilesEnabled && admin.role === "admin")
            }
            configuration={table.configuration}
            gameName="Blackjack"
            onActivate={(profile, reason) => activateProfile(table, profile, reason)}
            onCancelPending={(reason) => cancelPendingProfile(table, reason)}
            renderSettings={(profile) => (
              <>
                <div>
                  <dt>Player decision</dt>
                  <dd>{asDuration(profile.config.playerTurnTimeoutMs)}</dd>
                </div>
                <div>
                  <dt>Insurance</dt>
                  <dd>{asDuration(profile.config.insuranceDecisionTimeoutMs)}</dd>
                </div>
                <div>
                  <dt>Result display</dt>
                  <dd>{asDuration(profile.config.settlementPresentationMs)}</dd>
                </div>
                <div>
                  <dt>Main wager</dt>
                  <dd>{wagerBand(profile.config.mainWager, profile.config.currency.code)}</dd>
                </div>
                <div>
                  <dt>Perfect Pairs</dt>
                  <dd>
                    {wagerBand(
                      profile.config.sideWagers.perfectPairs,
                      profile.config.currency.code,
                    )}
                  </dd>
                </div>
                <div>
                  <dt>21+3</dt>
                  <dd>
                    {wagerBand(
                      profile.config.sideWagers.twentyOnePlusThree,
                      profile.config.currency.code,
                    )}
                  </dd>
                </div>
              </>
            )}
          />
          {table.currentRound ? (
            <div className="blackjack-live-round">
              <div>
                <span className="label-13 dim">Round state</span>
                <strong>{table.phase}</strong>
              </div>
              <div>
                <span className="label-13 dim">Card details</span>
                <strong>Available after settlement</strong>
              </div>
              <button
                className="btn btn-quiet"
                onClick={() => onOpenRound?.(table.currentRound?.roundId ?? "")}
                type="button"
              >
                Open round
              </button>
            </div>
          ) : null}
          <div className="table-scroll">
            <table className="poker-seats-table">
              <thead>
                <tr>
                  <th>Seat</th>
                  <th>Player</th>
                  <th>Connection</th>
                  <th>Ready</th>
                  <th>Round</th>
                </tr>
              </thead>
              <tbody>
                {table.seats.map((seat) => (
                  <tr key={seat.seatId}>
                    <td>
                      #{seat.seatIndex + 1}
                      <span className="cell-sub mono-12">{seat.seatId}</span>
                    </td>
                    <td>
                      {seat.displayName ?? "Unknown player"}
                      <span className="cell-sub mono-12">{seat.userId}</span>
                    </td>
                    <td>{seat.connected ? "Connected" : "Disconnected"}</td>
                    <td>{seat.ready ? "Ready" : "Waiting"}</td>
                    <td>
                      {seat.participating ? "Participating" : seat.leavePending ? "Leaving" : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div className="card table-scroll">
        <div className="card-bar">
          <strong>Recent rounds</strong>
          <span className="label-13 faint">Latest {data.recentRounds.length}</span>
        </div>
        <table className="poker-hands-table">
          <thead>
            <tr>
              <th>Started</th>
              <th>Round</th>
              <th>Players</th>
              <th>Status</th>
              <th>Stake</th>
            </tr>
          </thead>
          <tbody>
            {data.recentRounds.map((round) => (
              <tr className="clickable-row" key={round.id} onClick={() => onOpenRound?.(round.id)}>
                <td>{asMoment(round.startedAt)}</td>
                <td className="mono-12">{round.id}</td>
                <td>
                  {round.players.map((player) => player.displayName ?? player.email).join(", ")}
                </td>
                <td>
                  <span className="chip">{round.status}</span>
                </td>
                <td>{asAmount({ amount: round.stake, currency: round.currency })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
